/**
 * @fileoverview Estimated Outcome Waterfall
 * @module components/jobs/phases/overview/EstimatedOutcomeWaterfall
 *
 * Purpose:
 * - Surface a one-glance "where the money goes" summary on the job
 *   overview. Starts from total realisable assets, deducts the
 *   practitioner's fee estimate, then deducts creditor classes in the
 *   statutory priority order, and reveals the residual surplus or
 *   shortfall to ordinary unsecured creditors.
 *
 * Source-of-truth model (post Assets/Creditors SOT refactor):
 * - Every creditor-side line is derived from `liabilityEntries.amount`
 *   (the Creditors table), classified by what's linked to each row in
 *   `recordLinks`. The detail tables (`securityInterestEntries`,
 *   `loanEntries`, `atoSroEntries`) are NOT summed directly here — they
 *   only contribute classification metadata (e.g. authority = ato vs sro).
 *
 * Classification per Creditor row (priority order):
 *   1. Has a linked Security Interest    → Security Interests (priority)
 *   2. Else ranked preferential           → Preferred creditors
 *   3. Else has a linked ATO record       → ATO (unsecured)
 *   4. Else has a linked SRO record       → SRO (unsecured)
 *   5. Else has a linked Loan             → Loans (unsecured)
 *   6. Else                               → Creditors / general unsecured
 *
 * Order of deductions (s556 Corporations Act, simplified):
 *   1. Secured creditors           — netted against the asset(s) that secure
 *                                     them (see Secured netting below)
 *   2. Practitioner's Fees         — manually entered (only manual input)
 *   3. Preferred creditors         — employee entitlements, plus any creditor
 *                                     row the practitioner ranked preferential
 *   4. Unsecured (broken down):    — creditors with no linked SI, classified by
 *        a. ATO                       linked atoSroEntry (authority="ato")
 *        b. SRO                       linked atoSroEntry (authority starts "sro_")
 *        c. Loans                     linked loanEntry (and no ATO/SRO)
 *        d. Creditors / general       no linked SI, loan, or ATO/SRO
 *        e. Secured shortfall         secured debt exceeding its collateral
 *
 * Secured netting (MOR-526 — true value of assets):
 * - Each security interest's owed balance (the sum of `amount` on the creditor
 *   rows linked to it) is netted against the combined value of the asset(s)
 *   linked to it (group-by-SI). Recovery is capped at the collateral value, the
 *   surplus flows to the free pool (surfacing the asset's residual / "true"
 *   value), and any shortfall ranks pari passu with unsecured creditors.
 * - A secured creditor with no linked asset has no collateral to net against, so
 *   it keeps the legacy behaviour: a full secured-priority deduction.
 * - See `assetList/securedNetting.ts` (`computeSecuredNetting`), shared with the
 *   Asset list panel so both surfaces show the same residual figures.
 *
 * Visual:
 * - Chrome matches the sibling panels on the Overview/Plan page (Assets,
 *   General Liabilities, Preferred Creditors, etc.): the same
 *   `GROUP_CARD_CLASS` shell, a simple icon + title header, a shadcn
 *   `<Table>` body with the standard uppercase muted column heads, a bold
 *   `TOTAL` row, and a centered footer hint.
 * - Deduction blocks use the same affordances as the Assets summary group
 *   headers: coloured soft `Badge` chips, chevron expand/collapse, a small
 *   line count, section totals in the Amount column, and an optional
 *   expand/collapse-all control above the ledger.
 *
 * Notes / caveats:
 * - This is an indicative summary, not a full Estimated Outcome Statement.
 *   Secured shortfalls are now ranked with unsecured creditors, but it still
 *   does not apply circulating-asset (s561) priority rules. The footer surfaces
 *   a small disclaimer so practitioners don't mistake it for the formal s556
 *   dividend forecast.
 * - The practitioner's fee row is editable inline by owners and members
 *   (viewers can read but not change it). Persisted on
 *   `jobs.estimatedPractitionerFees` via `setEstimatedPractitionerFees`.
 *
 * Dependencies:
 * - Convex queries: listAssetEntriesForJob, listEmployeeEntriesForJob,
 *   listAtoSroEntriesForJob, listLiabilityEntriesForJob,
 *   listLinksForJob (recordLinks classifier)
 * - Convex mutation: setEstimatedPractitionerFees
 * - Asset valuation helper: getSelectedValue / getBestValue (re-used so we
 *   stay consistent with the Assets panel's "Total Value" figure).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Switch } from "@/ui/primitives/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Coins,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/utils/misc";
import {
  getRocapValue,
  getPractitionerValue,
  getLowRecovery,
  getAiEstimateValue,
  getHighRecovery,
  collectValuationSources,
  getValuationForSource,
  formatSourceType,
  isHiddenValuationSource,
} from "@/components/jobs/phases/gather/summarization/assetList/helpers";
import {
  computeSecuredNetting,
  type NettingLiability,
  type NettingLink,
  type NettingSecurityInterest,
} from "@/components/jobs/phases/gather/summarization/assetList/securedNetting";
import type { AssetEntryUI } from "@/components/jobs/phases/gather/summarization/assetList/types";
import type { EmployeeEntryUI } from "@/components/jobs/phases/gather/summarization/employment/types";
import { sumEmployeeEntitlements } from "@cvx/lib/employeeEntitlements";
import { usePersistedSet } from "@/utils/use-persisted-set";
import { directorDisclosureLabel } from "@/lib/director-disclosure";
import { WaterfallColumnsMenu } from "./WaterfallColumnsMenu";

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------
//
// We intentionally narrow the entries to just the fields the waterfall
// reads. The list queries return `v.array(v.any())` so picking a slim
// shape keeps the local code honest without coupling us to every panel's
// own UI types.

type LiabilityRow = {
  _id: Id<"liabilityEntries">;
  status: "active" | "merged";
  amount: number;
  creditorName: string;
  /**
   * Mirror flag maintained by `syncCreditorSecuredStatus` whenever an SI
   * link is added or removed. Equivalent to "is there an active SI link
   * for this creditor in recordLinks?".
   */
  secured?: boolean;
  /**
   * Set by the practitioner when the debt ranks ahead of ordinary unsecured
   * creditors under s556 — e.g. a preferred creditor added by hand, or a row
   * created from an employee's or priority proof of debt.
   */
  preferential?: boolean;
  /** Excluded creditors are dropped from every waterfall class. */
  isExcluded?: boolean;
};

/** A single named line item shown under an expanded ledger row. */
type DetailItem = { id: string; name: string; amount: number };

type AtoSroRow = {
  _id: Id<"atoSroEntries">;
  status: "active" | "merged";
  authority: string;
};

/**
 * One row from `listLinksForJob` — narrowed to the fields the waterfall
 * needs to classify each Creditor. `anchorType: "creditor"` is the only
 * shape we care about here; asset-side links don't affect the ledger.
 */
type RecordLinkRow = {
  detailType: "security_interest" | "loan" | "ato_sro";
  detailId: string;
  anchorType: "asset" | "creditor";
  anchorId: string;
};

// ---------------------------------------------------------------------------
// Shared card chrome — duplicated from plan.tsx so this component stays
// self-contained. Matches every sibling card on the Overview page.
// ---------------------------------------------------------------------------

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  return `${sign}$${abs.toLocaleString("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Bracketed format used for the deduction column, matching how
 * accountants render negative values on a ledger (e.g. `($12,345)`).
 */
function fmtDeduction(value: number): string {
  if (value === 0) return "—";
  return `(${fmtCurrency(value)})`;
}

// ---------------------------------------------------------------------------
// Stat card — mirrors the StatCard used by EmployeeEntitlementsPanel so the
// summary tiles feel native on the Overview page.
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string;
  accent?: "neutral" | "positive" | "negative";
}

function StatCard({ label, value, accent = "neutral" }: StatCardProps) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/30 px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 text-2xl font-semibold tabular-nums",
          accent === "positive" && "text-emerald-600 dark:text-emerald-500",
          accent === "negative" && "text-destructive",
          accent === "neutral" && "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ledger rows
// ---------------------------------------------------------------------------
//
// All ledger rows share the same four-column shape so the body lines up
// regardless of whether it's the opening row, a deduction, a subtotal,
// or a section header.

/**
 * The "running balance" cells shown on every ledger row — one per visible
 * column, in the same order as the parent's `visibleColumns`. The deduction
 * (`Amount`) is identical across columns; only these balances diverge because
 * each column opens from a different Total Realisable Assets figure. The
 * `balances` array is already narrowed to the visible columns by the parent.
 */
function ScenarioBalanceCells({
  balances,
  emphasis,
}: {
  balances: number[];
  emphasis?: boolean;
}) {
  return (
    <>
      {balances.map((b, i) => (
        <TableCell
          key={i}
          className={cn(
            "px-4 py-3 text-right tabular-nums",
            emphasis ? "text-sm font-semibold" : "text-xs",
            b < 0
              ? "text-destructive"
              : emphasis
                ? "text-foreground"
                : "text-muted-foreground",
          )}
        >
          {fmtCurrency(b)}
        </TableCell>
      ))}
    </>
  );
}

interface LedgerRowProps {
  label: string;
  /** Deduction / opening amount (scenario-independent). `null` renders "—". */
  amount: number | null;
  /** Running balance after this row, one per scenario (SCENARIO_META order). */
  scenarioBalances: number[];
  /** Render the amount in red brackets, e.g. `($12,345)`. */
  isDeduction?: boolean;
  /** Render the row indented (used for sub-items under a section header). */
  indented?: boolean;
  /** Render the row with the subtotal treatment (bold-ish, top border). */
  isSubtotal?: boolean;
  /** Helper text shown under the label, used for short clarifications. */
  hint?: string;
  /**
   * When provided, renders a "View detail / Hide detail" toggle next to the
   * label that expands the underlying line items (assets by category,
   * individual creditors / employees) inline beneath this row.
   */
  onToggleDetail?: () => void;
  /** Whether this row's inline detail is currently expanded. */
  detailExpanded?: boolean;
}

function LedgerRow({
  label,
  amount,
  scenarioBalances,
  isDeduction,
  indented,
  isSubtotal,
  hint,
  onToggleDetail,
  detailExpanded,
}: LedgerRowProps) {
  return (
    <TableRow
      className={cn(
        "hover:bg-muted/30",
        isSubtotal && "border-t border-border bg-muted/10 hover:bg-muted/10",
      )}
    >
      <TableCell className={cn("px-4 py-3", indented && "pl-10")}>
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "text-sm",
              isSubtotal
                ? "font-semibold text-foreground"
                : indented
                  ? "text-muted-foreground"
                  : "font-medium text-foreground",
            )}
          >
            {label}
          </div>
          {onToggleDetail && (
            <button
              type="button"
              onClick={onToggleDetail}
              aria-expanded={detailExpanded}
              className="inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] text-muted-foreground/70 transition-colors hover:text-foreground"
            >
              {detailExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {detailExpanded ? "Hide detail" : "View detail"}
            </button>
          )}
        </div>
        {hint && (
          <div className="mt-0.5 text-[10px] text-muted-foreground/80">
            {hint}
          </div>
        )}
      </TableCell>
      <TableCell
        className={cn(
          "px-4 py-3 text-right text-sm tabular-nums",
          isDeduction ? "text-destructive" : "text-foreground",
          isSubtotal && "font-semibold",
        )}
      >
        {amount == null
          ? "—"
          : isDeduction
            ? fmtDeduction(amount)
            : fmtCurrency(amount)}
      </TableCell>
      <ScenarioBalanceCells balances={scenarioBalances} emphasis={isSubtotal} />
    </TableRow>
  );
}

/**
 * One asset-category line under the expanded "Realisable Assets" row. Shows the
 * category's value per visible column (net-of-security in net mode, gross in
 * gross mode); the Amount column stays blank because these are components of
 * the opening balance, not deductions.
 */
function AssetCategoryRow({
  category,
  count,
  values,
}: {
  category: string;
  count: number;
  values: number[];
}) {
  return (
    <TableRow className="hover:bg-muted/20">
      <TableCell className="px-4 py-2 pl-14">
        <span className="text-xs text-muted-foreground">{category}</span>
        <span className="ml-1.5 text-[10px] text-muted-foreground/60">
          {count === 1 ? "1 asset" : `${count} assets`}
        </span>
      </TableCell>
      <TableCell className="px-4 py-2 text-right text-xs tabular-nums text-muted-foreground/40">
        —
      </TableCell>
      {values.map((v, i) => (
        <TableCell
          key={i}
          className="px-4 py-2 text-right text-xs tabular-nums text-muted-foreground"
        >
          {fmtCurrency(v)}
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * One named creditor / employee line under an expanded deduction row. The
 * amount sits in the Amount column (these are column-independent); the
 * running-balance columns stay blank because a single line item isn't itself a
 * step in the waterfall.
 */
function CreditorDetailRow({
  name,
  amount,
  balanceColumns,
}: {
  name: string;
  amount: number;
  balanceColumns: number;
}) {
  return (
    <TableRow className="hover:bg-muted/20">
      <TableCell className="px-4 py-2 pl-14">
        <span className="text-xs text-muted-foreground">{name}</span>
      </TableCell>
      <TableCell className="px-4 py-2 text-right text-xs tabular-nums text-destructive">
        {fmtDeduction(amount)}
      </TableCell>
      {Array.from({ length: balanceColumns }).map((_, i) => (
        <TableCell key={i} className="px-4 py-2" />
      ))}
    </TableRow>
  );
}

/** Stable ids for expand/collapse state. */
const WATERFALL_SECTION_IDS = [
  "secured",
  "costs",
  "priority",
  "unsecured",
] as const;
type WaterfallSectionId = (typeof WATERFALL_SECTION_IDS)[number];

interface WaterfallSectionHeaderProps {
  /** Chip text — short labels matching how practitioners scan the ledger. */
  chipLabel: string;
  badgeColor: BadgeColor;
  expanded: boolean;
  onToggle: () => void;
  /** Rows nested under this header (shown as “N lines”, like asset counts). */
  innerRowCount: number;
  /**
   * Sum of deductions inside this section (non-negative). `null` renders "—" —
   * used when the deduction varies by scenario (e.g. capped secured recovery),
   * in which case the per-scenario balance columns carry the figure instead.
   */
  deductionTotal: number | null;
  /** Running balance after this section, one per scenario (SCENARIO_META order). */
  scenarioBalances: number[];
}

/**
 * Group header matching `AssetListPanel` `GroupHeaderRow`: chevron, soft
 * badge chip, small grey count, section deduction total right-aligned in
 * the Amount column, running balance in the last column.
 */
function WaterfallSectionHeader({
  chipLabel,
  badgeColor,
  expanded,
  onToggle,
  innerRowCount,
  deductionTotal,
  scenarioBalances,
}: WaterfallSectionHeaderProps) {
  const linesLabel = innerRowCount === 1 ? "1 line" : `${innerRowCount} lines`;

  return (
    <TableRow
      className="cursor-pointer select-none border-t border-border bg-muted/40 hover:bg-muted/50"
      aria-expanded={expanded}
      data-state={expanded ? "open" : "closed"}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      tabIndex={0}
      role="button"
    >
      <TableCell className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <Badge color={badgeColor} variant="soft" size="sm">
            {chipLabel}
          </Badge>
          <span className="text-xs font-medium text-muted-foreground">
            {linesLabel}
          </span>
        </div>
      </TableCell>
      <TableCell className="px-4 py-2.5 text-right">
        <span className="text-sm font-semibold tabular-nums text-destructive">
          {deductionTotal == null ? "—" : fmtDeduction(deductionTotal)}
        </span>
      </TableCell>
      <ScenarioBalanceCells balances={scenarioBalances} emphasis />
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Practitioner Fees row (the only manually-edited line on the waterfall)
// ---------------------------------------------------------------------------

interface PractitionerFeesRowProps {
  jobId: Id<"jobs">;
  /** Current persisted value, or `undefined` when not yet set. */
  value: number | undefined;
  /** Running balance after this row, one per scenario (SCENARIO_META order). */
  scenarioBalances: number[];
  /** When `false`, render read-only (viewers). */
  canEdit: boolean;
}

/**
 * Inline editor for the practitioner's fee estimate. Sits inside the
 * ledger table as a normal `<TableRow>` with a pencil affordance and an
 * inline input that commits on Enter or blur.
 *
 * Edit lifecycle:
 * - Click the value (or "Add an estimate" button) → input appears with the
 *   current value pre-filled
 * - Enter or blur → save (debounced through the mutation)
 * - Escape → cancel and revert to the persisted value
 *
 * The mutation rejects negatives + non-finite numbers, so we mirror that
 * client-side and surface a toast on failure.
 */
function PractitionerFeesRow({
  jobId,
  value,
  scenarioBalances,
  canEdit,
}: PractitionerFeesRowProps) {
  // Amount column + one cell per visible value column — the edit input spans
  // all of them.
  const editColSpan = 1 + scenarioBalances.length;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    value === undefined ? "" : String(value),
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync the draft if the persisted value updates while we're not editing
  // (e.g. another tab edited it). When editing, leave the user's draft alone.
  useEffect(() => {
    if (!editing) {
      setDraft(value === undefined ? "" : String(value));
    }
  }, [value, editing]);

  // Auto-focus + select-all when entering edit mode so the practitioner
  // can immediately type a replacement.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const { mutateAsync: saveFees, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.mutations.setEstimatedPractitionerFees,
    ),
  });

  const commit = async () => {
    const trimmed = draft.trim();
    const nextValue: number | undefined =
      trimmed === "" ? undefined : Number(trimmed.replace(/[$,\s]/g, ""));
    if (
      nextValue !== undefined &&
      (!Number.isFinite(nextValue) || nextValue < 0)
    ) {
      toast.error("Enter a positive amount or leave blank to clear");
      return;
    }

    if (nextValue === value) {
      setEditing(false);
      return;
    }

    try {
      await saveFees({ jobId, fees: nextValue });
      setEditing(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save fee estimate",
      );
    }
  };

  const cancel = () => {
    setDraft(value === undefined ? "" : String(value));
    setEditing(false);
  };

  const isUnset = value === undefined;
  const displayAmount = value ?? 0;

  return (
    <TableRow className="hover:bg-muted/30">
      <TableCell className="px-4 py-3 pl-10">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Practitioner&apos;s Fees
          </span>
          {canEdit && !editing && !isUnset && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground/60 transition-colors hover:text-foreground"
              aria-label="Edit practitioner's fees"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="mt-0.5 text-[10px] text-muted-foreground/80">
          Estimated total cost of the appointment — only manually-entered line
        </div>
      </TableCell>

      {editing ? (
        <TableCell colSpan={editColSpan} className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            <span className="text-xs text-muted-foreground">$</span>
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commit();
                if (e.key === "Escape") cancel();
              }}
              onBlur={() => void commit()}
              disabled={isPending}
              placeholder="0"
              className="h-7 w-32 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              // Prevent the mousedown from blurring the input first (which
              // would commit + close before the click registers).
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void commit()}
              disabled={isPending}
              aria-label="Save practitioner's fees"
            >
              {isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancel}
              disabled={isPending}
              aria-label="Cancel"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </TableCell>
      ) : isUnset ? (
        <>
          <TableCell className="px-4 py-3 text-right">
            {canEdit ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Plus className="h-3 w-3" />
                Add an estimate
              </button>
            ) : (
              <span className="text-sm tabular-nums text-muted-foreground">
                —
              </span>
            )}
          </TableCell>
          <ScenarioBalanceCells balances={scenarioBalances} />
        </>
      ) : (
        <>
          <TableCell className="px-4 py-3 text-right">
            <button
              type="button"
              onClick={() => canEdit && setEditing(true)}
              disabled={!canEdit}
              className={cn(
                "text-sm tabular-nums text-destructive",
                canEdit
                  ? "-mx-1 cursor-text rounded px-1 transition-colors hover:bg-muted/50"
                  : "cursor-default",
              )}
            >
              {fmtDeduction(displayAmount)}
            </button>
          </TableCell>
          <ScenarioBalanceCells balances={scenarioBalances} />
        </>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface EstimatedOutcomeWaterfallProps {
  jobId: Id<"jobs">;
  /**
   * Manually-entered practitioner fee estimate persisted on the job row.
   * `undefined` (or omitted) means "not yet set" — the row renders an
   * "Add" affordance instead of a deduction.
   */
  estimatedPractitionerFees: number | undefined;
  /**
   * When `false`, the fee field renders read-only (no inline editor or
   * "Add" button). Wired from the parent based on `job.userRole !== "viewer"`.
   */
  canEditFees: boolean;
  /**
   * Deprecated / unused. The per-row "View detail" links now expand the
   * underlying line items inline instead of navigating to the agent panel.
   * Kept on the props so existing call sites stay valid.
   */
  onViewAgentDetail?: (agentId: string) => void;
}

/**
 * The asset-value scenarios the waterfall can be computed for. Each sums a
 * different per-asset figure into "Total Realisable Assets"; the entire
 * creditor side is identical across scenarios.
 */
type RecoveryScenario = "rocap" | "practitioner" | "low" | "high" | "ai";

const SCENARIO_META: Array<{ id: RecoveryScenario; label: string }> = [
  { id: "rocap", label: "ROCAP" },
  { id: "practitioner", label: "Practitioner" },
  { id: "low", label: "Low" },
  { id: "high", label: "High" },
  { id: "ai", label: "AI Estimate" },
];

/**
 * Static `sm:grid-cols-*` classes keyed by visible-column count, so the summary
 * tile row stays balanced as scenarios are toggled off. Literal strings so
 * Tailwind keeps them in the build.
 */
const SM_GRID_COLS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  5: "sm:grid-cols-5",
};

/** Per-asset value selector for each scenario (null contributes $0). */
const SCENARIO_SELECTOR: Record<
  RecoveryScenario,
  (a: AssetEntryUI) => number | null
> = {
  rocap: getRocapValue,
  practitioner: getPractitionerValue,
  low: getLowRecovery,
  high: getHighRecovery,
  ai: getAiEstimateValue,
};

/**
 * A waterfall column: a labelled per-asset value selector. The whole ledger
 * (secured netting → creditor stack) is computed independently for each one,
 * so a column can be a recovery scenario or a valuation source — they're
 * indistinguishable to the maths.
 */
type WaterfallColumn = {
  /** "rocap" | … for scenarios, or `src:<sourceType>` for valuation sources. */
  id: string;
  label: string;
  selector: (a: AssetEntryUI) => number | null;
};

/** Prefix marking a column id as a valuation source (vs a recovery scenario). */
const SOURCE_COLUMN_PREFIX = "src:";

/**
 * Columns shown when a job has no saved preference: the recovery scenarios.
 * Valuation-source columns stay hidden until explicitly toggled on so the
 * waterfall doesn't balloon with every source the moment one appears. Jobs
 * with a stored column preference won't show newly-added scenarios (e.g.
 * "AI Estimate") until the practitioner toggles them on.
 */
const DEFAULT_WATERFALL_COLUMN_IDS: string[] = SCENARIO_META.map((s) => s.id);

/**
 * A draggable value-column header. Reordering persists to the shared view
 * (see `handleColumnDragEnd`), so it is disabled for viewers and when only one
 * column is visible. `canReorder === false` renders a plain, static header.
 */
function SortableColumnHead({
  id,
  label,
  canReorder,
}: {
  id: string;
  label: string;
  canReorder: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !canReorder });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn(
        "h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        isDragging && "z-10 bg-muted",
      )}
      title={
        canReorder
          ? `Running balance under the ${label} column — drag to reorder`
          : `Running balance under the ${label} column`
      }
    >
      <span className="inline-flex items-center justify-end gap-1">
        {canReorder && (
          <button
            type="button"
            className="cursor-grab touch-none text-muted-foreground/40 transition-colors hover:text-foreground active:cursor-grabbing"
            aria-label={`Drag to reorder the ${label} column`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-3" />
          </button>
        )}
        {label}
      </span>
    </TableHead>
  );
}

export function EstimatedOutcomeWaterfall({
  jobId,
  estimatedPractitionerFees,
  canEditFees,
}: EstimatedOutcomeWaterfallProps) {
  // MOR-626 — sub-sections collapsed by default; the user's expand/collapse
  // choice persists globally to localStorage.
  const [expandedSections, setExpandedSections] = usePersistedSet(
    "overview:waterfall:expanded-sections",
  );
  // Per-row inline detail expansion ("View detail" on a ledger line reveals the
  // underlying assets-by-category / individual creditors). Persisted locally,
  // same as the section state — the choice carries across reloads, per user.
  const [expandedDetails, setExpandedDetails] = usePersistedSet(
    "overview:waterfall:expanded-details",
  );
  const toggleDetail = (id: string) =>
    setExpandedDetails((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // When on (default), each asset is shown net of the security registered
  // against it (residual / "true" value); secured recovery is capped at the
  // collateral value and any shortfall ranks with unsecured creditors. When off,
  // assets are shown gross and secured creditors are deducted in full as a class.
  const [netOffSecurity, setNetOffSecurity] = useState(true);

  const toggleSection = (id: WaterfallSectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSectionsExpanded = WATERFALL_SECTION_IDS.every((id) =>
    expandedSections.has(id),
  );

  const toggleAllSections = () => {
    setExpandedSections(
      allSectionsExpanded ? new Set() : new Set(WATERFALL_SECTION_IDS),
    );
  };

  // Shared per-job view prefs (same `jobs.assetViewPrefs` the Asset table uses)
  // — drives which waterfall columns are visible. Toggling a column is
  // display-only; it never affects any figure.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const updateViewPrefs = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateAssetViewPrefs),
  });

  // -------------------------------------------------------------------------
  // Data fetching — re-uses the same public queries as each detail panel so
  // the totals stay reactive and consistent with what the practitioner
  // sees inside the per-agent panels.
  // -------------------------------------------------------------------------

  const { data: assetEntries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAssetEntriesForJob, {
      jobId,
    }),
  );

  const { data: employeeEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listEmployeeEntriesForJob,
      { jobId },
    ),
  );

  // ATO/SRO entries are still fetched, but only to map a linked
  // atoSroEntry id → "ato" | "sro" for classifying the linked Creditor.
  // The dollar figures themselves come from `liabilityEntries.amount`.
  const { data: atoSroEntries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoSroEntriesForJob, {
      jobId,
    }),
  );

  const { data: liabilityEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId },
    ),
  );

  // Active recordLinks for the job — used to classify each Creditor by
  // what's linked to it (SI / ATO/SRO / Loan / nothing).
  const { data: recordLinks } = useQuery(
    convexQuery(
      api.jobs.summarization.recordLinks.linkQueries.listLinksForJob,
      {
        jobId,
      },
    ),
  );
  // Security interests carry their own owed figure, which the netting uses as
  // the canonical debt (see `computeSecuredNetting`).
  const { data: securityInterestEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listSecurityInterestEntriesForJob,
      { jobId },
    ),
  );

  const isLoading =
    assetEntries === undefined ||
    employeeEntries === undefined ||
    atoSroEntries === undefined ||
    liabilityEntries === undefined ||
    recordLinks === undefined ||
    securityInterestEntries === undefined;

  // -------------------------------------------------------------------------
  // Columns — the waterfall can be computed for any per-asset value. Two kinds:
  //   • recovery scenarios (ROCAP / Practitioner / Low / High / AI Estimate) —
  //     fixed, always offered.
  //   • valuation sources (Balance Sheet, Bank Statement, …) — the same columns
  //     the Asset table exposes, discovered from the job's assets. Each opens
  //     its waterfall from that source's per-asset value.
  // Every column is just a `(asset) => number | null` selector fed through the
  // same secured-netting + creditor stack, so they compute identically.
  // -------------------------------------------------------------------------
  // The "rocap" scenario is relabelled per appointment type (ROCAP for
  // corporate insolvencies, SOA for bankruptcies, …). The column id stays
  // "rocap" so stored view preferences remain valid — only the label changes.
  const disclosure = directorDisclosureLabel(job?.appointmentType);
  const scenarioColumns = useMemo<WaterfallColumn[]>(
    () =>
      SCENARIO_META.map((s) => ({
        id: s.id,
        label: s.id === "rocap" ? disclosure.short : s.label,
        selector: SCENARIO_SELECTOR[s.id],
      })),
    [disclosure.short],
  );
  const sourceColumns = useMemo<WaterfallColumn[]>(() => {
    if (assetEntries === undefined) return [];
    // Exclude the raw "ROCAP" and "Director Estimate" sources — the fixed ROCAP
    // scenario column already shows the resolved director figure, so those raw
    // source columns render as a redundant second director total (matches the
    // asset table).
    return collectValuationSources(assetEntries as AssetEntryUI[])
      .filter((source) => !isHiddenValuationSource(source))
      .map((source) => ({
        id: `${SOURCE_COLUMN_PREFIX}${source}`,
        label: formatSourceType(source),
        selector: (e: AssetEntryUI) => getValuationForSource(e, source),
      }));
  }, [assetEntries]);
  // Scenarios first, then valuation sources — stable display order regardless
  // of the order columns were toggled on.
  const availableColumns = useMemo(
    () => [...scenarioColumns, ...sourceColumns],
    [scenarioColumns, sourceColumns],
  );

  // The persisted column order (shared per job). `undefined` until the job
  // query resolves; falls back to the default scenarios.
  const storedWaterfallOrder = job?.assetViewPrefs?.visibleWaterfallColumns;

  // Optimistic column order applied the instant a drag/toggle happens, before
  // the Convex round-trip lands. Without it the header snaps back to the old
  // order on drop and only jumps to the new position once the query updates.
  // Cleared as soon as the server confirms the same order (or it diverges,
  // e.g. another user reordered), so external changes still flow through.
  const [pendingWaterfallOrder, setPendingWaterfallOrder] = useState<
    string[] | null
  >(null);
  useEffect(() => {
    if (pendingWaterfallOrder == null) return;
    if (
      storedWaterfallOrder != null &&
      storedWaterfallOrder.length === pendingWaterfallOrder.length &&
      storedWaterfallOrder.every((id, i) => id === pendingWaterfallOrder[i])
    ) {
      setPendingWaterfallOrder(null);
    }
  }, [storedWaterfallOrder, pendingWaterfallOrder]);

  const effectiveWaterfallOrder =
    pendingWaterfallOrder ??
    storedWaterfallOrder ??
    DEFAULT_WATERFALL_COLUMN_IDS;

  // Visible columns: the effective order (optimistic override, else the stored
  // list) mapped to available columns — a stale id whose source was deleted is
  // dropped. Never empty — falls back to the recovery scenarios in their
  // canonical order.
  const visibleColumns = useMemo(() => {
    const byId = new Map(availableColumns.map((c) => [c.id, c]));
    const shown = effectiveWaterfallOrder.flatMap((id) => {
      const col = byId.get(id);
      return col ? [col] : [];
    });
    if (shown.length > 0) return shown;
    return availableColumns.filter((c) =>
      DEFAULT_WATERFALL_COLUMN_IDS.includes(c.id),
    );
  }, [effectiveWaterfallOrder, availableColumns]);
  const visibleColumnIds = visibleColumns.map((c) => c.id);

  // Persist a new column order and apply it optimistically in one step. Each
  // commit takes a sequence number so a slow, failed request can't clear the
  // optimistic state of a newer reorder; on failure the pending order is
  // dropped (falling back to the stored order) and the practitioner is told.
  const waterfallOrderReq = useRef(0);
  const commitWaterfallOrder = (next: string[]) => {
    setPendingWaterfallOrder(next);
    const req = ++waterfallOrderReq.current;
    updateViewPrefs
      .mutateAsync({
        jobId,
        visibleWaterfallColumns: next,
      })
      .catch(() => {
        if (waterfallOrderReq.current !== req) return;
        setPendingWaterfallOrder(null);
        toast.error("Couldn't save the column order — please try again.");
      });
  };

  const toggleColumn = (id: string) => {
    const availableIds = new Set(availableColumns.map((c) => c.id));
    const current = effectiveWaterfallOrder.filter((cid) =>
      availableIds.has(cid),
    );
    const next = current.includes(id)
      ? current.filter((c) => c !== id)
      : [...current, id];
    // Keep at least one column on screen — an empty waterfall is meaningless.
    if (next.length === 0) return;
    commitWaterfallOrder(next);
  };

  // Reordering the visible columns is a shared view change (like toggling),
  // gated on write access. Persisting the reordered id list is enough — the
  // header, summary tiles and every body balance cell all render in
  // `visibleColumns` order.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Keyboard reordering: the sortable header button is focusable, so without
    // this sensor keyboard users could focus it but never move a column.
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = visibleColumnIds.indexOf(String(active.id));
    const to = visibleColumnIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    commitWaterfallOrder(arrayMove(visibleColumnIds, from, to));
  };

  // -------------------------------------------------------------------------
  // Totals
  // -------------------------------------------------------------------------

  const base = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const assets = assetEntries as AssetEntryUI[];
    const liabilities = liabilityEntries as NettingLiability[];
    const links = recordLinks as NettingLink[];
    const sis = securityInterestEntries as NettingSecurityInterest[];

    // ----- Secured netting, one pass per column ---------------------------
    // Each column opens from a different per-asset value, so the residual /
    // capped recovery / shortfall all differ by column. `computeSecuredNetting`
    // nets each security interest's owed balance against the combined value of
    // the asset(s) linked to it (group-by-SI), capping recovery at collateral
    // and pushing any shortfall down to rank with unsecured creditors. We
    // compute every AVAILABLE column (not just the visible ones) so toggling a
    // column on is instant and doesn't re-run the netting.
    const grossByColumn: Record<string, number> = {};
    const recoveredByColumn: Record<string, number> = {};
    const shortfallByColumn: Record<string, number> = {};
    // Encumbered assetId → net-of-security residual value, per column. Used by
    // the expandable asset breakdown so each category's net value matches the
    // Asset list's per-asset "true value" and reconciles to the net opening.
    const residualByColumn: Record<string, Map<string, number>> = {};
    // Depends only on owed amounts, not asset values, so it's identical across
    // columns — captured once below from the first column's netting result.
    let unlinkedSecured = 0;
    availableColumns.forEach((col, i) => {
      const netting = computeSecuredNetting(
        assets,
        liabilities,
        sis,
        links,
        col.selector,
      );
      grossByColumn[col.id] = netting.totalGrossAssets;
      recoveredByColumn[col.id] = netting.totalRecovered;
      shortfallByColumn[col.id] = netting.totalShortfall;
      residualByColumn[col.id] = netting.residualByAsset;
      if (i === 0) unlinkedSecured = netting.totalUnlinkedSecured;
    });
    // Any column gives the same secured claim (recovered + shortfall = owed,
    // column-independent). Fall back to the first column id.
    const refColumnId = availableColumns[0]?.id;

    // ----- Creditor side — preferred + unsecured buckets ------------------
    // The preferred class has two sources: employee entitlements from
    // `employeeEntries`, and creditor rows the practitioner has ranked as
    // preferred (or that came in on an employee / priority proof of debt).
    //
    // NOTE (waterfall audit): the liabilities agent is instructed to keep
    // employee-related items OUT of `liabilityEntries` (see
    // agents/liabilities/manifest prompt), so the two sources shouldn't
    // overlap. That is a prompt-level guarantee, not a structural one — a
    // preferred creditor row for an entitlement already in the Employees
    // register is counted twice, which is why both the add form and rows
    // created from an employee's proof say so on their face.
    const preferredItems: DetailItem[] = [];
    const employees = (employeeEntries as EmployeeEntryUI[]).reduce(
      (sum, entry) => {
        if (entry.status !== "active" || entry.isExcluded) return sum;
        const total = sumEmployeeEntitlements(entry.entitlements);
        if (total > 0) {
          preferredItems.push({
            id: entry._id,
            name: entry.name,
            amount: total,
          });
        }
        return sum + total;
      },
      0,
    );

    // ----- Build link classifier per Creditor row -------------------------
    // For each active Creditor we want to know: has a linked SI? has a
    // linked Loan? has a linked ATO/SRO record (and which authority)?
    // `recordLinks` only returns active links so we can read them straight.
    const atoSroAuthorityById = new Map<string, "ato" | "sro">();
    for (const a of atoSroEntries as AtoSroRow[]) {
      if (a.status !== "active") continue;
      if (a.authority === "ato") atoSroAuthorityById.set(a._id, "ato");
      else if (a.authority.startsWith("sro_"))
        atoSroAuthorityById.set(a._id, "sro");
    }

    type CreditorFlags = {
      hasSI: boolean;
      hasAto: boolean;
      hasSro: boolean;
      hasLoan: boolean;
    };
    const flagsByCreditor = new Map<string, CreditorFlags>();
    const ensureFlags = (id: string): CreditorFlags => {
      const existing = flagsByCreditor.get(id);
      if (existing) return existing;
      const created: CreditorFlags = {
        hasSI: false,
        hasAto: false,
        hasSro: false,
        hasLoan: false,
      };
      flagsByCreditor.set(id, created);
      return created;
    };
    for (const link of recordLinks as RecordLinkRow[]) {
      if (link.anchorType !== "creditor") continue;
      const flags = ensureFlags(link.anchorId);
      switch (link.detailType) {
        case "security_interest":
          flags.hasSI = true;
          break;
        case "loan":
          flags.hasLoan = true;
          break;
        case "ato_sro": {
          const authority = atoSroAuthorityById.get(link.detailId);
          if (authority === "ato") flags.hasAto = true;
          else if (authority === "sro") flags.hasSro = true;
          else {
            // Linked but the referenced atoSroEntry is missing/inactive —
            // treat as a generic ATO bucket so the figure isn't lost.
            flags.hasAto = true;
          }
          break;
        }
      }
    }

    // ----- Classify each active Creditor into exactly one bucket ----------
    // Secured creditors are handled entirely by the netting above (their
    // recovery is netted against collateral; any shortfall ranks unsecured via
    // `shortfallByColumn`). Here we only classify the NON-secured creditors
    // into ATO → SRO → Loan → general unsecured.
    let ato = 0;
    let sro = 0;
    let loans = 0;
    let general = 0;
    let preferredCreditors = 0;
    const atoItems: DetailItem[] = [];
    const sroItems: DetailItem[] = [];
    const loanItems: DetailItem[] = [];
    const generalItems: DetailItem[] = [];
    for (const liab of liabilityEntries as LiabilityRow[]) {
      if (liab.status !== "active" || liab.isExcluded) continue;
      const flags = flagsByCreditor.get(liab._id);
      const amount = liab.amount ?? 0;
      const isSecured = liab.secured === true || flags?.hasSI === true;
      const item: DetailItem = {
        id: liab._id,
        name: liab.creditorName,
        amount,
      };
      if (isSecured) {
        continue; // handled by secured netting
      } else if (liab.preferential) {
        // Ranks above the unsecured pool, so it is deducted with the
        // employees rather than classified by what it is linked to.
        preferredCreditors += amount;
        preferredItems.push(item);
      } else if (flags?.hasAto) {
        ato += amount;
        atoItems.push(item);
      } else if (flags?.hasSro) {
        sro += amount;
        sroItems.push(item);
      } else if (flags?.hasLoan) {
        loans += amount;
        loanItems.push(item);
      } else {
        general += amount;
        generalItems.push(item);
      }
    }
    // Largest first — the detail list reads top-down by materiality.
    for (const list of [
      atoItems,
      sroItems,
      loanItems,
      generalItems,
      preferredItems,
    ])
      list.sort((a, b) => b.amount - a.amount);

    const practitionerFees = Math.max(0, estimatedPractitionerFees ?? 0);
    const preferred = employees + preferredCreditors;
    const unsecuredBase = ato + sro + general + loans;
    // Total secured creditor claim (owed) is column-independent because
    // recovered + shortfall === owed for every column.
    const securedClaim =
      refColumnId !== undefined
        ? recoveredByColumn[refColumnId] +
          shortfallByColumn[refColumnId] +
          unlinkedSecured
        : unlinkedSecured;
    const totalCreditors = securedClaim + preferred + unsecuredBase;

    return {
      grossByColumn,
      recoveredByColumn,
      shortfallByColumn,
      residualByColumn,
      unlinkedSecured,
      securedClaim,
      practitionerFees,
      employees,
      preferredCreditors,
      preferred,
      ato,
      sro,
      general,
      loans,
      unsecuredBase,
      totalCreditors,
      // Per-line detail for the expandable ledger rows.
      preferredItems,
      atoItems,
      sroItems,
      loanItems,
      generalItems,
    };
  }, [
    isLoading,
    estimatedPractitionerFees,
    assetEntries,
    employeeEntries,
    atoSroEntries,
    liabilityEntries,
    recordLinks,
    securityInterestEntries,
    availableColumns,
  ]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (isLoading || !base) {
    return (
      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-center gap-2.5 px-5 py-3">
          <Coins className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Estimated Outcome</h4>
          <Loader2 className="ml-auto h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // The visible columns, side by side, in `visibleColumns` order. Every running
  // balance array below is positional over this list, so hiding a column simply
  // drops it from each array; the figures for the remaining columns are
  // unchanged.
  const colIds = visibleColumnIds;

  // Realisable assets grouped by category, one value per visible column. In net
  // mode each asset contributes its net-of-security residual (gross when
  // unencumbered), so the category totals reconcile to the Net Realisable
  // Assets opening; in gross mode each contributes its gross value. Same active,
  // non-excluded asset set the secured netting uses.
  const assetCategoryRows = assetEntries
    ? (() => {
        const cats = new Map<string, { count: number; values: number[] }>();
        for (const a of assetEntries as AssetEntryUI[]) {
          if (a.status !== "active" || a.isExcluded) continue;
          const existing = cats.get(a.assetType);
          const row = existing ?? { count: 0, values: colIds.map(() => 0) };
          if (!existing) cats.set(a.assetType, row);
          row.count += 1;
          visibleColumns.forEach((col, i) => {
            const grossV = col.selector(a) ?? 0;
            const v = netOffSecurity
              ? (base.residualByColumn[col.id]?.get(a._id) ?? grossV)
              : grossV;
            row.values[i] += v;
          });
        }
        return [...cats.entries()]
          .map(([category, r]) => ({
            category,
            count: r.count,
            values: r.values,
          }))
          .sort((a, b) => (b.values[0] ?? 0) - (a.values[0] ?? 0));
      })()
    : [];

  // Running balances down the priority stack. The shape depends on the gross/net
  // toggle, but the final outcome is identical either way (secured owed =
  // recovered + shortfall + unlinked).
  //
  //   NET (default): open from each asset's value LESS the security registered
  //     against it (residual). Linked secured recovery is netted into the
  //     opening; only secured creditors with no linked collateral are deducted
  //     as a class, and any secured shortfall ranks with unsecured creditors.
  //   GROSS: open from gross asset values and deduct the full secured claim as a
  //     single class line (the familiar, un-netted view).
  const gross = colIds.map((id) => base.grossByColumn[id] ?? 0);

  // Deduction taken in the Secured Creditors section (column-independent in
  // both modes, so the Amount column reads cleanly):
  //   NET   → only the unlinked secured creditors (linked ones are netted above)
  //   GROSS → the full secured claim
  const securedDeduction = netOffSecurity
    ? base.unlinkedSecured
    : base.securedClaim;

  const openingBalances = netOffSecurity
    ? colIds.map((id, i) => gross[i] - (base.recoveredByColumn[id] ?? 0))
    : gross;
  const afterSecured = openingBalances.map((b) => b - securedDeduction);
  const afterFees = afterSecured.map((b) => b - base.practitionerFees);
  const afterPreferred = afterFees.map((b) => b - base.preferred);
  const afterAto = afterPreferred.map((b) => b - base.ato);
  const afterSro = afterAto.map((b) => b - base.sro);
  const afterLoans = afterSro.map((b) => b - base.loans);
  const afterGeneral = afterLoans.map((b) => b - base.general);
  // Secured shortfall only appears in net mode (in gross mode the full claim was
  // already deducted in the Secured section).
  const outcomes = netOffSecurity
    ? colIds.map((id, i) => afterGeneral[i] - (base.shortfallByColumn[id] ?? 0))
    : afterGeneral;

  // In net mode the linked secured recovery is already baked into the opening
  // "Net Realisable Assets" figure, so the only thing left to show as a ledger
  // deduction is the unlinked secured creditors. In gross mode we show the full
  // secured claim as a class line.
  const hasSecuredSection = netOffSecurity
    ? base.unlinkedSecured > 0
    : base.securedClaim > 0;
  const hasShortfall =
    netOffSecurity &&
    colIds.some((id) => (base.shortfallByColumn[id] ?? 0) > 0);

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Header — matches the Entity / Appointment / Team cards on this
          page (no status pill or "View detail" link because this isn't an
          agent — it's a derived summary). */}
      <div className="flex items-center gap-2.5 px-5 py-3">
        <Coins className="h-4 w-4 shrink-0 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Estimated Outcome</h4>
        <span className="ml-auto rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
          Total creditor claims {fmtCurrency(base.totalCreditors)}
        </span>
      </div>

      <div className="border-t border-border">
        {/* Estimated outcome per column — one tile per visible ledger column so
            the user can compare the chosen outcomes side by side at a glance. */}
        <div
          className={cn(
            "grid grid-cols-2 gap-3 px-5 pb-4 pt-5",
            SM_GRID_COLS[visibleColumns.length] ?? "sm:grid-cols-4",
          )}
        >
          {visibleColumns.map((col, i) => {
            const o = outcomes[i];
            return (
              <StatCard
                key={col.id}
                label={`${col.label} outcome`}
                value={`${o < 0 ? "Shortfall " : "Surplus "}${fmtCurrency(
                  Math.abs(o),
                )}`}
                accent={o >= 0 ? "positive" : "negative"}
              />
            );
          })}
        </div>

        {/* Net-off-security toggle + expand/collapse all. */}
        <div className="flex items-center justify-between border-t border-border px-5 py-2">
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={netOffSecurity}
              onCheckedChange={setNetOffSecurity}
              aria-label="Net off security interests"
            />
            <span className="font-medium">Net off security interests</span>
            <span className="text-muted-foreground/70">
              {netOffSecurity
                ? "— assets shown net of secured debt"
                : "— assets shown gross"}
            </span>
          </label>
          <div className="flex items-center gap-2">
            <WaterfallColumnsMenu
              groups={[
                { label: "Recovery scenarios", columns: scenarioColumns },
                { label: "Valuation sources", columns: sourceColumns },
              ]}
              visibleIds={visibleColumnIds}
              onToggle={toggleColumn}
              disabled={!canEditFees}
            />
            <button
              type="button"
              onClick={toggleAllSections}
              className="flex items-center gap-1 whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronsUpDown className="h-3 w-3" />
              {allSectionsExpanded ? "Collapse all" : "Expand all"}
            </button>
          </div>
        </div>

        {/* Ledger */}
        <div className="border-t border-border">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-transparent">
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Description
                  </TableHead>
                  <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Amount
                  </TableHead>
                  <SortableContext
                    items={visibleColumnIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    {visibleColumns.map((col) => (
                      <SortableColumnHead
                        key={col.id}
                        id={col.id}
                        label={col.label}
                        canReorder={canEditFees && visibleColumns.length > 1}
                      />
                    ))}
                  </SortableContext>
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Opening row: gross total realisable assets (per scenario).
                  Secured netting is applied in the Secured Creditors section
                  below; the running balance after that section is the residual
                  (net-of-security) realisable figure. */}
                <LedgerRow
                  label={
                    netOffSecurity
                      ? "Net Realisable Assets"
                      : "Total Realisable Assets"
                  }
                  amount={null}
                  scenarioBalances={openingBalances}
                  onToggleDetail={
                    assetCategoryRows.length > 0
                      ? () => toggleDetail("assets")
                      : undefined
                  }
                  detailExpanded={expandedDetails.has("assets")}
                  hint={
                    netOffSecurity
                      ? "Each asset shown net of the security registered against it (residual / true value)"
                      : "Gross realisable value (security shown as a deduction below)"
                  }
                />
                {expandedDetails.has("assets") &&
                  assetCategoryRows.map((c) => (
                    <AssetCategoryRow
                      key={c.category}
                      category={c.category}
                      count={c.count}
                      values={c.values}
                    />
                  ))}

                {hasSecuredSection && (
                  <>
                    <WaterfallSectionHeader
                      chipLabel="Secured Creditors"
                      badgeColor="orange"
                      expanded={expandedSections.has("secured")}
                      onToggle={() => toggleSection("secured")}
                      innerRowCount={1}
                      deductionTotal={securedDeduction}
                      scenarioBalances={afterSecured}
                    />
                    {expandedSections.has("secured") &&
                      (netOffSecurity ? (
                        <LedgerRow
                          label="Secured creditors (no linked asset)"
                          amount={base.unlinkedSecured}
                          scenarioBalances={afterSecured}
                          isDeduction
                          indented
                          hint="No collateral linked — deducted in full. Link the securing asset to net it off. Linked secured creditors are already netted into Net Realisable Assets above."
                        />
                      ) : (
                        <LedgerRow
                          label="Secured creditors"
                          amount={base.securedClaim}
                          scenarioBalances={afterSecured}
                          isDeduction
                          indented
                          hint="Creditors with a linked security interest — deducted in full. Turn on 'Net off security' to net them against their collateral."
                        />
                      ))}
                  </>
                )}

                <WaterfallSectionHeader
                  chipLabel="Appointment Costs"
                  badgeColor="violet"
                  expanded={expandedSections.has("costs")}
                  onToggle={() => toggleSection("costs")}
                  innerRowCount={1}
                  deductionTotal={base.practitionerFees}
                  scenarioBalances={afterFees}
                />
                {expandedSections.has("costs") && (
                  <PractitionerFeesRow
                    jobId={jobId}
                    value={estimatedPractitionerFees}
                    scenarioBalances={afterFees}
                    canEdit={canEditFees}
                  />
                )}

                <WaterfallSectionHeader
                  chipLabel="Priority Creditors"
                  badgeColor="amber"
                  expanded={expandedSections.has("priority")}
                  onToggle={() => toggleSection("priority")}
                  innerRowCount={1}
                  deductionTotal={base.preferred}
                  scenarioBalances={afterPreferred}
                />
                {expandedSections.has("priority") && (
                  <>
                    <LedgerRow
                      label="Preferred Creditors"
                      amount={base.preferred}
                      scenarioBalances={afterPreferred}
                      isDeduction
                      indented
                      onToggleDetail={
                        base.preferredItems.length > 0
                          ? () => toggleDetail("preferred")
                          : undefined
                      }
                      detailExpanded={expandedDetails.has("preferred")}
                      hint={
                        base.preferred === 0
                          ? "No preferred creditor entitlements captured"
                          : base.preferredCreditors > 0
                            ? "Employee entitlements and preferred creditors"
                            : "Wages, super, leave, PILON and redundancy"
                      }
                    />
                    {expandedDetails.has("preferred") &&
                      base.preferredItems.map((it) => (
                        <CreditorDetailRow
                          key={it.id}
                          name={it.name}
                          amount={it.amount}
                          balanceColumns={colIds.length}
                        />
                      ))}
                  </>
                )}

                <WaterfallSectionHeader
                  chipLabel="Unsecured Creditors"
                  badgeColor="sky"
                  expanded={expandedSections.has("unsecured")}
                  onToggle={() => toggleSection("unsecured")}
                  innerRowCount={hasShortfall ? 6 : 5}
                  deductionTotal={hasShortfall ? null : base.unsecuredBase}
                  scenarioBalances={outcomes}
                />
                {expandedSections.has("unsecured") && (
                  <>
                    <LedgerRow
                      label="ATO"
                      amount={base.ato}
                      scenarioBalances={afterAto}
                      isDeduction
                      indented
                      onToggleDetail={
                        base.atoItems.length > 0
                          ? () => toggleDetail("ato")
                          : undefined
                      }
                      detailExpanded={expandedDetails.has("ato")}
                      hint="Creditors with a linked ATO record"
                    />
                    {expandedDetails.has("ato") &&
                      base.atoItems.map((it) => (
                        <CreditorDetailRow
                          key={it.id}
                          name={it.name}
                          amount={it.amount}
                          balanceColumns={colIds.length}
                        />
                      ))}
                    <LedgerRow
                      label="SRO"
                      amount={base.sro}
                      scenarioBalances={afterSro}
                      isDeduction
                      indented
                      onToggleDetail={
                        base.sroItems.length > 0
                          ? () => toggleDetail("sro")
                          : undefined
                      }
                      detailExpanded={expandedDetails.has("sro")}
                      hint="Creditors with a linked SRO record (payroll tax, stamp duty, land tax)"
                    />
                    {expandedDetails.has("sro") &&
                      base.sroItems.map((it) => (
                        <CreditorDetailRow
                          key={it.id}
                          name={it.name}
                          amount={it.amount}
                          balanceColumns={colIds.length}
                        />
                      ))}
                    <LedgerRow
                      label="Loans"
                      amount={base.loans}
                      scenarioBalances={afterLoans}
                      isDeduction
                      indented
                      onToggleDetail={
                        base.loanItems.length > 0
                          ? () => toggleDetail("loans")
                          : undefined
                      }
                      detailExpanded={expandedDetails.has("loans")}
                      hint="Unsecured Creditors with a linked loan record"
                    />
                    {expandedDetails.has("loans") &&
                      base.loanItems.map((it) => (
                        <CreditorDetailRow
                          key={it.id}
                          name={it.name}
                          amount={it.amount}
                          balanceColumns={colIds.length}
                        />
                      ))}
                    <LedgerRow
                      label="Creditors"
                      amount={base.general}
                      scenarioBalances={afterGeneral}
                      isDeduction
                      indented
                      onToggleDetail={
                        base.generalItems.length > 0
                          ? () => toggleDetail("general")
                          : undefined
                      }
                      detailExpanded={expandedDetails.has("general")}
                      hint="Unsecured Creditors with no linked SI, loan, or ATO/SRO"
                    />
                    {expandedDetails.has("general") &&
                      base.generalItems.map((it) => (
                        <CreditorDetailRow
                          key={it.id}
                          name={it.name}
                          amount={it.amount}
                          balanceColumns={colIds.length}
                        />
                      ))}
                    {hasShortfall && (
                      <LedgerRow
                        label="Secured shortfall"
                        amount={null}
                        scenarioBalances={outcomes}
                        isDeduction
                        indented
                        hint="Secured debt exceeding the value of its collateral — ranks pari passu with unsecured creditors"
                      />
                    )}
                    <LedgerRow
                      label="Subtotal — unsecured creditors"
                      amount={hasShortfall ? null : base.unsecuredBase}
                      scenarioBalances={outcomes}
                      isDeduction
                      isSubtotal
                      hint="All unsecured creditors rank pari passu"
                    />
                  </>
                )}

                {/* Final result — bold outcome row with one cell per scenario. */}
                <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                  <TableCell className="px-4 py-3 text-sm font-bold text-foreground">
                    Estimated Outcome to Creditors
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground/40">
                    —
                  </TableCell>
                  {outcomes.map((o, i) => (
                    <TableCell
                      key={i}
                      className={cn(
                        "px-4 py-3 text-right text-sm font-bold tabular-nums",
                        o >= 0
                          ? "text-emerald-600 dark:text-emerald-500"
                          : "text-destructive",
                      )}
                    >
                      {fmtCurrency(o)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </DndContext>
        </div>
      </div>

      {/* Footer hint — same `border-t border-border px-5 py-3` + centered
          muted text used by every sibling panel. */}
      <div className="border-t border-border px-5 py-3">
        <p className="text-center text-xs text-muted-foreground">
          Each column is the running balance under a different asset basis —
          {` ${disclosure.short}`} value, practitioner valuation, the low / high
          recovery estimates, and the AI estimate. The Amount column is the
          deduction taken at each row. Drag a column header to reorder the
          columns.
          {netOffSecurity
            ? " Assets are shown net of the security registered against them; secured shortfalls rank with unsecured creditors."
            : " Assets are shown gross, with secured creditors deducted in full."}{" "}
          Indicative only — recalculates as agent panels update.
        </p>
      </div>
    </div>
  );
}
